/**
 * Eine Varianten-Karte: Titel/SKU · Preisebenen · Gewicht + Grundpreis.
 * Karte statt 9-spaltigem Grid → passt in 768 px Sheet und aufs Handy.
 */
import { Trash2, Store } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { parseDecimal } from '@/lib/productEditorModel';
import {
  GRUNDPREIS_EINHEITEN, REFERENZ_OPTIONEN, computeGrundpreis, defaultReferenz, einheitLabel, formatGrundpreis,
} from '@/lib/grundpreis';
import { Field, NumInput, TextInput } from './EditorPrimitives';

const NONE = '__none__';
const basisOf = (v) => GRUNDPREIS_EINHEITEN.find((e) => e.value === v)?.basis || null;

export default function VariantRowCard({ row, index, productPreis, linked, onChange, onRemove, canRemove }) {
  const basis = basisOf(row.grundpreis_einheit);
  const refOptions = REFERENZ_OPTIONEN.filter((o) => !basis || basisOf(o.einheit) === basis);
  const refValue = row.grundpreis_referenz_einheit
    ? `${row.grundpreis_referenz_menge || 1}|${row.grundpreis_referenz_einheit}` : NONE;

  const onlinePreis = parseDecimal(row.online_preis);
  const grundpreis = computeGrundpreis({
    preis: onlinePreis, menge: parseDecimal(row.grundpreis_menge), einheit: row.grundpreis_einheit,
    refMenge: parseDecimal(row.grundpreis_referenz_menge), refEinheit: row.grundpreis_referenz_einheit,
  });

  const setEinheit = (v) => {
    if (v === NONE) {
      onChange({ grundpreis_einheit: '', grundpreis_referenz_menge: '', grundpreis_referenz_einheit: '' });
      return;
    }
    const patch = { grundpreis_einheit: v };
    if (basisOf(row.grundpreis_referenz_einheit) !== basisOf(v)) {
      const d = defaultReferenz(v);
      patch.grundpreis_referenz_menge = d ? String(d.menge) : '';
      patch.grundpreis_referenz_einheit = d ? d.einheit : '';
    }
    onChange(patch);
  };
  const setReferenz = (v) => {
    if (v === NONE) { onChange({ grundpreis_referenz_menge: '', grundpreis_referenz_einheit: '' }); return; }
    const [menge, einheit] = v.split('|');
    onChange({ grundpreis_referenz_menge: menge, grundpreis_referenz_einheit: einheit });
  };

  return (
    <div className="rounded-lg border border-slate-200 bg-white p-3 space-y-3">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 min-w-0">
          <span className="text-[11px] font-mono text-slate-400">#{index + 1}</span>
          {row.is_default && <span className="text-[10px] uppercase tracking-wide text-slate-500 border border-slate-200 rounded px-1.5 py-0.5">Standard</span>}
          {row.shopify_variant_id && (
            <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wide text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-1.5 py-0.5">
              <Store className="w-3 h-3" /> Shopify
            </span>
          )}
          {row.bestand != null && <span className="text-[11px] text-slate-500 tabular-nums">Bestand {row.bestand}</span>}
        </div>
        {canRemove && (
          <Button type="button" variant="ghost" size="icon" onClick={onRemove} aria-label="Variante entfernen"
            className="h-9 w-9 text-slate-400 hover:text-red-600 hover:bg-red-50">
            <Trash2 className="w-4 h-4" />
          </Button>
        )}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-[1.4fr_1fr] gap-3">
        <Field label="Variante">
          <TextInput value={row.title} onChange={(e) => onChange({ title: e.target.value })} placeholder="Standard" />
        </Field>
        <Field label="SKU">
          <TextInput value={row.sku} onChange={(e) => onChange({ sku: e.target.value })} className="font-mono" />
        </Field>
      </div>

      <div className="grid grid-cols-3 gap-3">
        <Field label="Online €" hint={linked ? '→ Shopify' : null}>
          <NumInput value={row.online_preis} onChange={(e) => onChange({ online_preis: e.target.value })} placeholder="—" />
        </Field>
        <Field label="Streich €" hint={linked ? '→ Shopify' : null}>
          <NumInput value={row.compare_at_preis} onChange={(e) => onChange({ compare_at_preis: e.target.value })} placeholder="—" />
        </Field>
        <Field label="B2B €" hint="bleibt privat">
          <NumInput value={row.b2b_preis} onChange={(e) => onChange({ b2b_preis: e.target.value })} placeholder={productPreis || '—'} />
        </Field>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-[1fr_1fr_1fr_1.2fr] gap-3 pt-3 border-t border-slate-100">
        <Field label="Gewicht kg" hint={linked ? '→ Shopify' : 'für EWK-Abgabe'}>
          <NumInput value={row.gewicht_kg} onChange={(e) => onChange({ gewicht_kg: e.target.value })} placeholder="0,000" />
        </Field>
        <Field label="Inhalt / Menge">
          <div className="flex gap-1">
            <NumInput value={row.grundpreis_menge} onChange={(e) => onChange({ grundpreis_menge: e.target.value })} placeholder="500" className="min-w-0" />
            <Select value={row.grundpreis_einheit || NONE} onValueChange={setEinheit}>
              <SelectTrigger className="min-h-[44px] w-[84px] shrink-0"><SelectValue placeholder="Einh." /></SelectTrigger>
              <SelectContent>
                <SelectItem value={NONE}>—</SelectItem>
                {GRUNDPREIS_EINHEITEN.map((e) => <SelectItem key={e.value} value={e.value}>{e.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </Field>
        <Field label="Referenz">
          <Select value={refValue} onValueChange={setReferenz} disabled={!row.grundpreis_einheit}>
            <SelectTrigger className="min-h-[44px]"><SelectValue placeholder="je …" /></SelectTrigger>
            <SelectContent>
              <SelectItem value={NONE}>—</SelectItem>
              {refOptions.map((o) => (
                <SelectItem key={`${o.menge}|${o.einheit}`} value={`${o.menge}|${o.einheit}`}>
                  je {o.menge === 1 ? '' : `${o.menge} `}{einheitLabel(o.einheit)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Field>
        <Field label="Grundpreis" hint={row.grundpreis_einheit && onlinePreis == null ? 'Online-Preis fehlt' : null}>
          <div className="min-h-[44px] flex items-center justify-between gap-2 rounded-md border border-slate-200 bg-slate-50 px-3">
            <span className="font-mono text-sm tabular-nums text-brand-950">
              {formatGrundpreis(grundpreis, row.grundpreis_referenz_menge, row.grundpreis_referenz_einheit)}
            </span>
            <label className="flex items-center gap-1.5 text-[11px] text-slate-500 cursor-pointer select-none">
              <Checkbox checked={!!row.grundpreis_anzeigen} onCheckedChange={(v) => onChange({ grundpreis_anzeigen: !!v })} aria-label="Grundpreis im Shop anzeigen" />
              im Shop
            </label>
          </div>
        </Field>
      </div>
    </div>
  );
}
